import type { CommercialOrderApi } from './types';

export function useCommercialOrderApi(): CommercialOrderApi {
  const { ensureAuthenticated } = useDjustAuth();

  /**
   * Get a commercial order
   * @param params
   * @param config
   * @returns
   */
  const getCommercialOrder = async (
    params: GetCommercialOrderRequest,
    config?: Partial<DjustConfig>
  ): Promise<GetCommercialOrderResponse> => {
    try {
      await ensureAuthenticated();

      const { orderId, locale, idType, nbPreviewLines } = params;

      if (!orderId) throw new Error('The "orderId" parameter is required.');

      const { data, error } = await useFetch<GetCommercialOrderResponse>(
        `/api/commercial-orders/${orderId}`,
        {
          query: { locale, idType, nbPreviewLines, ...config },
        }
      );

      if (error.value) throw new Error('Failed to retrieve commercial order.');

      return data.value as GetCommercialOrderResponse;
    } catch (e) {
      console.error('Failed to retrieve commercial order:', (e as ApiError).message || e);
      throw new Error('Failed to retrieve commercial order. Please try again later.');
    }
  };

  /**
   * Create a commercial order
   * @param params
   * @param config
   * @returns
   */
  const createCommercialOrder = async (
    params: CreateCommercialOrderRequest,
    config?: Partial<DjustConfig>
  ): Promise<CreateCommercialOrderResponse> => {
    try {
      await ensureAuthenticated();

      const { nbPreviewLines, channel, customFields, locale, origin, originId, paymentInfo } =
        params;

      const { data, error } = await useFetch<CreateCommercialOrderResponse>(
        '/api/commercial-orders',
        {
          method: 'POST',
          query: config,
          body: {
            nbPreviewLines,
            channel,
            customFields,
            locale,
            origin,
            originId,
            paymentInfo,
          },
        }
      );

      if (error.value) throw new Error('Failed to create commercial order.');

      return data.value as CreateCommercialOrderResponse;
    } catch (e) {
      console.error('Failed to create commercial order:', (e as ApiError).message || e);
      throw new Error('Failed to create commercial order. Please try again later.');
    }
  };

  /**
   * Update the shipping address of a commercial order
   * @param params
   * @param config
   */
  const updateCommercialOrderShippingAddress = async (
    params: UpdateCommercialOrderShippingAddressRequest,
    config?: Partial<DjustConfig>
  ): Promise<updateCommercialOrderShippingAddressResponse> => {
    try {
      await ensureAuthenticated();

      const { orderId, shippingAddressId } = params;

      if (!orderId) throw new Error('The "orderId" parameter is required.');
      if (!shippingAddressId) throw new Error('The "shippingAddressId" parameter is required.');

      const { data, error } = await useFetch<updateCommercialOrderShippingAddressResponse>(
        `/api/commercial-orders/${orderId}/shipping-address`,
        {
          method: 'PUT',
          query: config,
          body: { shippingAddressId },
        }
      );

      if (error.value) throw new Error('Failed to update shipping address.');

      return data.value as updateCommercialOrderShippingAddressResponse;
    } catch (e) {
      console.error('Failed to update shipping address:', (e as ApiError).message || e);
      throw new Error('Failed to update shipping address. Please try again later.');
    }
  };

  /**
   * Update the shipping informations for a commercial order
   * @param params
   * @param config
   */
  const updateCommercialOrderShippingInfo = async (
    params: UpdateCommercialOrderShippingInformationRequest,
    config?: Partial<DjustConfig>
  ): Promise<updateCommercialOrderShippingAddressInfoResponse> => {
    try {
      await ensureAuthenticated();

      const { orderId, shippingAddressId, shippingType } = params;

      if (!orderId) throw new Error('The "orderId" parameter is required.');
      if (!shippingAddressId) throw new Error('The "shippingAddressId" parameter is required.');
      if (!shippingType) throw new Error('The "shippingType" parameter is required.');

      const { data, error } = await useFetch<updateCommercialOrderShippingAddressInfoResponse>(
        `/api/commercial-orders/${orderId}/shipping-information`,
        {
          method: 'PUT',
          query: config,
          body: { shippingAddressId, shippingType },
        }
      );

      if (error.value) throw new Error('Failed to update shipping address.');

      return data.value as updateCommercialOrderShippingAddressInfoResponse;
    } catch (e) {
      console.error('Failed to update shipping address:', (e as ApiError).message || e);
      throw new Error('Failed to update shipping address. Please try again later.');
    }
  };

  /**
   * Update the billing information for a commercial order
   * @param params
   * @param config
   */
  const updateCommercialOrderBillingInformation = async (
    params: UpdateCommercialOrderBillingInformationRequest,
    config?: Partial<DjustConfig>
  ): Promise<updateCommercialOrderBillingInformationResponse> => {
    try {
      await ensureAuthenticated();

      const { orderId, billingAddressId } = params;

      if (!orderId) throw new Error('The "orderId" parameter is required.');
      if (!billingAddressId) throw new Error('The "billingAddressId" parameter is required.');

      const { data, error } = await useFetch<updateCommercialOrderBillingInformationResponse>(
        `/api/commercial-orders/${orderId}/billing-information`,
        {
          method: 'PUT',
          query: config,
          body: { billingAddressId },
        }
      );

      if (error.value) throw new Error('Failed to update billing information.');

      return data.value as updateCommercialOrderBillingInformationResponse;
    } catch (e) {
      console.error('Failed to update billing information:', (e as ApiError).message || e);
      throw new Error('Failed to update billing information. Please try again later.');
    }
  };

  /**
   * Set the status of a commercial order to "created"
   * @param params
   * @param config
   */
  const setCommercialOrderStatusAsCreated = async (
    params: SetCommercialOrderStatusAsCreatedRequest,
    config?: Partial<DjustConfig>
  ): Promise<setCommercialOrderStatusAsCreatedResponse> => {
    try {
      await ensureAuthenticated();

      const { orderId, paymentInfo } = params;

      if (!orderId) throw new Error('The "orderId" parameter is required.');

      const { data, error } = await useFetch<setCommercialOrderStatusAsCreatedResponse>(
        `/api/commercial-orders/${orderId}/created`,
        {
          method: 'PUT',
          query: config,
          body: { paymentInfo },
        }
      );

      if (error.value) throw new Error("Failed to set order status as 'created'.");

      return data.value as setCommercialOrderStatusAsCreatedResponse;
    } catch (e) {
      console.error("Failed to set order status as 'created':", (e as ApiError).message || e);
      throw new Error("Failed to set order status as 'created'. Please try again later.");
    }
  };

  const getCommercialOrderPunchout = async (
    params: GetCommercialOrderPunchoutRequest,
    config?: Partial<DjustConfig>
  ): Promise<any> => {
    try {
      await ensureAuthenticated();

      const { orderId, locale, tenantConfigurationKey } = params;

      if (!orderId) throw new Error('The "orderId" parameter is required.');
      if (!locale) throw new Error('The "locale" parameter is required.');

      const { data, error } = await useFetch<GetCommercialOrderPunchoutResponse>(
        `/api/commercial-orders/${orderId}/punchout`,
        {
          query: { locale, tenantConfigurationKey, ...config },
        }
      );

      if (error.value) throw new Error('Failed to retrieve commercial order punchout.');

      return data.value;
    } catch (e) {
      console.error('Failed to retrieve commercial order punchout:', (e as ApiError).message || e);
    }
  }

  return {
    getCommercialOrder,
    createCommercialOrder,
    updateCommercialOrderShippingAddress,
    updateCommercialOrderShippingInfo,
    updateCommercialOrderBillingInformation,
    setCommercialOrderStatusAsCreated,
    getCommercialOrderPunchout
  };
}
